import React from 'react';
import { motion } from 'framer-motion';

function RetirementPlanning() {
  const pillars = [
    { title: 'Income Strategy', points: ['Sustainable withdrawal rate', 'Sequence‑of‑returns defense', 'Annuity/ladder options'] },
    { title: 'Tax Efficiency', points: ['Roth vs Traditional mix', 'Asset location', 'Tax‑smart withdrawals'] },
    { title: 'Risk Management', points: ['Longevity risk', 'Healthcare/long‑term care', 'Downside buffers'] },
    { title: 'Legacy Choice', points: ['Beneficiary designations', 'Gifting tactics', 'Estate coordination'] },
  ];

  return (
    <div className="min-h-screen bg-white">
      <section className="relative h-[55vh] overflow-hidden">
        <img src="/images/business-people-hiring-job-offer-with-group-documents-contract-employment-b2b-deal-interview-staff-employees-teamwork-merger-project-human-resources-with-paperwork_590464-342156.jpg_tupxel.jpg" alt="Retirement planning" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/60 to-black/80" />
        <div className="relative z-10 flex h-full items-center justify-center text-center px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <h1 className="text-white text-5xl font-light mb-4">Retirement <span className="font-semibold text-[#fbbf24]">Planning</span></h1>
            <p className="text-white/90 text-xl max-w-3xl">Turn savings into reliable income—organized, tax‑smart and built to last.</p>
          </motion.div>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div data-aos="fade-up" className="text-center mb-14">
            <h2 className="text-3xl font-semibold text-gray-900">Your Retirement, De‑Risked</h2>
            <p className="text-gray-600 text-lg mt-2">A plan for income, taxes, risk and legacy—coordinated end‑to‑end.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {pillars.map((p, i) => (
              <div key={p.title} data-aos="fade-up" data-aos-delay={`${150 + i * 100}`} className="p-8 bg-white rounded-2xl shadow-lg border border-gray-100">
                <div className="w-12 h-12 rounded-full bg-[#0056d7] text-white font-semibold flex items-center justify-center mb-5">{String(i+1).padStart(2,'0')}</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{p.title}</h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  {p.points.map(pt => (<li key={pt} className="flex items-start gap-2"><span className="mt-1 w-2 h-2 rounded-full bg-[#0056d7]" />{pt}</li>))}
                </ul>
              </div>
            ))}
          </div>

          <div className="mt-14 text-center">
            <motion.a href="/contact" className="inline-block px-10 py-4 rounded-full bg-gradient-to-r from-[#fbbf24] to-[#f59e0b] text-black font-medium shadow-lg hover:shadow-amber-500/30 transition-colors" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.96 }}>
              Start Your Retirement Plan
            </motion.a>
          </div>
        </div>
      </section>

      {/* Investment Building Blocks */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="max-w-7xl mx-auto px-6">
          <div data-aos="fade-up" className="text-center mb-14">
            <h2 className="text-3xl font-semibold text-gray-900">Building Reliable Income</h2>
            <p className="text-gray-600 text-lg mt-2">Blend growth, stability and liquidity according to your timeline.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[{t:'Core Growth',d:'Global equity & multi‑asset sleeves to outpace inflation.'},{t:'Stability',d:'Bonds, ladders and structured notes for smoother ride.'},{t:'Liquidity',d:'Cash & short‑term reserves for near‑term spending.'}].map((b,i)=>(
              <div key={b.t} data-aos="fade-up" data-aos-delay={`${150+i*100}`} className="p-8 bg-white rounded-2xl shadow-lg border border-gray-100">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{b.t}</h3>
                <p className="text-gray-600">{b.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div data-aos="fade-up" className="text-center mb-10">
            <h2 className="text-3xl font-semibold text-gray-900">Retirement FAQs</h2>
          </div>
          <div className="space-y-6">
            {[
              {q:'What withdrawal rate do you use?', a:'We run Monte Carlo and guardrail approaches to set a sustainable, flexible withdrawal range.'},
              {q:'How do you manage sequence risk?', a:'By setting cash flow buffers and dynamic rebalancing to avoid selling risk assets at lows.'},
              {q:'Can you coordinate my pension/SS?', a:'Yes, we model options to maximize lifetime benefits within your plan.'}
            ].map((f,i)=> (
              <div key={f.q} data-aos="fade-up" data-aos-delay={`${100+i*100}`} className="p-6 rounded-xl bg-gray-50 border border-gray-100">
                <div className="font-semibold text-gray-900 mb-1">{f.q}</div>
                <div className="text-gray-700">{f.a}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export default RetirementPlanning;


